import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Gift, Target, Loader2, Plus, Pencil } from 'lucide-react';
import Modal from '../ui/Modal';
import PrimaryButton from '../ui/PrimaryButton';
import { toast } from 'sonner';

interface NewTrophyModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: {
        name: string;
        value: number;
        prize: string;
    } | null;
    onSubmit: (data: { name: string; value: number; prize: string }) => Promise<void>;
}

const NewTrophyModal: React.FC<NewTrophyModalProps> = ({ isOpen, onClose, initialData, onSubmit }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    // Form State
    const [name, setName] = useState('');
    const [value, setValue] = useState('');
    const [prize, setPrize] = useState('');

    // Reset or Populate form on open
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setName(initialData.name);
                setValue(initialData.value.toString());
                setPrize(initialData.prize);
            } else {
                setName('');
                setValue('');
                setPrize('');
            }
        }
    }, [isOpen, initialData]);

    // Validation Logic
    const isValidName = name.trim().length > 0;
    const isValidprize = prize.trim().length > 0;
    const isValidValue = !isNaN(Number(value)) && Number(value) > 0;

    const isFormValid = isValidName && isValidprize && isValidValue;

    const handleSubmit = async () => {
        if (!isFormValid) return;

        setIsLoading(true);
        try {
            await onSubmit({
                name,
                value: parseInt(value),
                prize
            });
            onClose();
        } catch (error) {
            console.error(error);
            toast.error(t('common.error'));
        } finally {
            setIsLoading(false);
        }
    };

    const isEditing = !!initialData;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            headerContent={
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        {isEditing ? t('salon.trophies.modal.title_edit') : t('salon.trophies.modal.title_new')}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                        {t('salon.trophies.modal.subtitle')}
                    </p>
                </div>
            }
        >
            <div className="space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* 1. Nome Livello */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <Trophy size={14} />
                            {t('salon.trophies.modal.name_label')}
                        </label>
                        <input
                            type="text"
                            placeholder={t('salon.trophies.modal.name_placeholder')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`w-full p-3 bg-gray-50 border rounded-xl focus:ring-2 outline-none font-medium text-gray-900 transition-all ${name && !isValidName ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-clip-200'}`}
                        />
                    </div>

                    {/* 2. Prenotazioni Richieste */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <Target size={14} />
                            {t('salon.trophies.modal.value_label')}
                        </label>
                        <input
                            type="number"
                            placeholder={t('salon.trophies.modal.value_placeholder')}
                            min="1"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            className={`w-full p-3 bg-gray-50 border rounded-xl focus:ring-2 outline-none font-medium text-gray-900 transition-all ${value && !isValidValue ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-clip-200'}`}
                        />
                    </div>
                </div>

                {/* 3. Premio (Full Width) */}
                <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Gift size={14} />
                        {t('salon.trophies.modal.prize_label')}
                    </label>
                    <input
                        type="text"
                        placeholder={t('salon.trophies.modal.prize_placeholder')}
                        value={prize}
                        onChange={(e) => setPrize(e.target.value)}
                        className={`w-full p-3 bg-gray-50 border rounded-xl focus:ring-2 outline-none font-medium text-gray-900 transition-all ${prize && !isValidprize ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-clip-200'}`}
                    />
                </div>

                {/* Footer Buttons */}
                <div className="pt-6 mt-2 border-t border-gray-100 flex gap-4">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                        type="button"
                    >
                        {t('common.cancel')}
                    </button>

                    <PrimaryButton
                        label={isLoading ? t('common.saving') : (isEditing ? t('salon.trophies.modal.save_btn') : t('salon.trophies.modal.create_btn'))}
                        className={`flex-1 justify-center ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={handleSubmit}
                        disabled={!isFormValid || isLoading}
                        showLabelOnMobile={true}
                        icon={isLoading ? Loader2 : undefined}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default NewTrophyModal;